import { BadgeCheck, Building2, FileCheck2, Search, Sparkles, Stamp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Carrossel do painel da marca.
 *
 * Existe por dois motivos, e o segundo é o que decidiu a forma: dizer mais do
 * produto sem crescer, e **devolver altura ao cartão**. Três diferenciais
 * empilhados custavam ~200px; um por vez custa ~64px, e é o que tira o scroll
 * de telas de 13 polegadas.
 *
 * Todo o texto já existia na tela — os três diferenciais e os três chips do
 * rodapé, que sumiram de lá porque repetiam informação. Nada de copy nova.
 *
 * Acessibilidade, e aqui há uma armadilha conhecida: carrossel que gira sozinho
 * e se anuncia rouba a leitura de quem está preenchendo o formulário ao lado.
 * Por isso `aria-live="off"` enquanto gira sozinho, e `polite` só quando a
 * pessoa clica num ponto — aí ela PEDIU para saber o que mudou.
 *
 * Para o resto:
 *  - pausa ao passar o mouse e ao receber foco pelo teclado;
 *  - `prefers-reduced-motion` / `[data-movimento='reduzido']` param a rotação e
 *    o carrossel vira uma lista comum — o conteúdo continua todo alcançável,
 *    que é o que importa;
 *  - os pontos são `<button>` de verdade, com nome dizendo o slide.
 */

const SLIDES = [
  {
    Icone: Search,
    titulo: 'Conferência automática',
    texto: 'O sistema acha o que ninguém lê duas vezes.',
  },
  {
    Icone: Sparkles,
    titulo: 'Consolidação sem redigitar',
    texto: 'A alteração vira texto sozinha.',
  },
  {
    Icone: FileCheck2,
    titulo: 'Documento pronto para a Junta',
    texto: '.docx editável e PDF.',
  },
  {
    Icone: Stamp,
    titulo: 'Primeira via',
    texto: 'Sem retrabalho no protocolo.',
  },
  {
    Icone: Building2,
    titulo: 'Multiempresa',
    texto: 'Uma conta, várias sociedades.',
  },
  {
    Icone: BadgeCheck,
    titulo: 'Conferido',
    texto: 'Bate com o contrato anterior.',
  },
] as const;

const INTERVALO_MS = 5000;

/**
 * Lê a preferência UMA vez, no primeiro render, e não observa depois.
 *
 * Testa `typeof window.matchMedia`, não só `typeof window`: jsdom tem `window`
 * e NÃO tem `matchMedia`, e a checagem pela metade derrubou 6 testes com
 * "matchMedia is not a function". Fora do teste vale o mesmo — o carrossel é
 * enfeite, e enfeite não pode derrubar a tela de login por causa de uma API
 * que o ambiente não tem.
 */
function movimentoReduzido(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return document.documentElement.dataset['movimento'] === 'reduzido';
  }
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.dataset['movimento'] === 'reduzido'
  );
}

export function CarrosselMarca(): JSX.Element {
  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);
  // `useRef` e não estado: mudar isto não precisa repintar, e ele só distingue
  // "o slide trocou sozinho" de "a pessoa pediu" para o leitor de tela.
  const pedido = useRef(false);
  const reduzido = useRef(movimentoReduzido()).current;

  useEffect(() => {
    if (reduzido || pausado) return;
    const id = window.setInterval(
      () => setIndice((i) => (i + 1) % SLIDES.length),
      INTERVALO_MS,
    );
    return () => window.clearInterval(id);
  }, [reduzido, pausado]);

  // Com movimento reduzido não há carrossel: é a lista inteira, de uma vez.
  if (reduzido) {
    return (
      <ul className="flex list-none flex-col gap-4">
        {SLIDES.slice(0, 3).map(({ Icone, titulo, texto }) => (
          <li key={titulo} className="flex items-start gap-3">
            <Moldura Icone={Icone} />
            <Texto titulo={titulo} texto={texto} />
          </li>
        ))}
      </ul>
    );
  }

  const atual = SLIDES[indice]!;

  return (
    <div
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocusCapture={() => setPausado(true)}
      onBlurCapture={() => setPausado(false)}
    >
      {/*
        Altura fixa: sem ela o cartão inteiro cresce e encolhe a cada troca,
        e o formulário ao lado dança junto.
      */}
      <div
        className="flex h-16 items-start gap-3"
        aria-live={pedido.current ? 'polite' : 'off'}
      >
        <Moldura Icone={atual.Icone} />
        {/*
          `key` no indice: força o React a trocar o nó, e a animação de entrada
          roda de novo. Sem isso o texto muda sem transição nenhuma.
        */}
        <div key={indice} className="animate-auth-entrada">
          <Texto titulo={atual.titulo} texto={atual.texto} />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2" role="tablist" aria-label="Destaques">
        {SLIDES.map((s, i) => (
          <button
            key={s.titulo}
            type="button"
            role="tab"
            aria-selected={i === indice}
            aria-label={s.titulo}
            onClick={() => {
              pedido.current = true;
              setIndice(i);
            }}
            className={[
              'focus-visible:ring-auth-borda-foco h-1.5 rounded-full',
              'transition-[width,background-color] duration-300 ease-out',
              'focus-visible:ring-2 focus-visible:outline-none',
              i === indice
                ? 'bg-auth-marca-hover w-6'
                : 'bg-auth-borda/30 hover:bg-auth-borda/60 w-1.5',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  );
}

function Moldura({ Icone }: { Icone: (typeof SLIDES)[number]['Icone'] }): JSX.Element {
  return (
    <span className="border-auth-borda/10 bg-auth-superficie/5 grid size-8 shrink-0 place-items-center rounded-lg border">
      <Icone className="text-auth-acento size-4" strokeWidth={1.8} aria-hidden />
    </span>
  );
}

function Texto({ titulo, texto }: { titulo: string; texto: string }): JSX.Element {
  return (
    <>
      <b className="text-auth-texto block text-sm font-medium">{titulo}</b>
      <p className="text-auth-texto-secundario mt-0.5 text-xs leading-relaxed">{texto}</p>
    </>
  );
}
